import { FC, useEffect, useState } from 'react';
import { Alert, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgEasSyncAction, TPgPaymentDetails } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { humanize } from '../common/tableHelpers';

type TEasSyncDialogProps = {
	open: boolean;
	payment: TPgPaymentDetails;
	onClose: () => void;
	onDone: (payment: TPgPaymentDetails) => void;
};

/**
 * Resolution of a payment whose "paid" mark did not reach EAS (`easSyncStatus` `conflict` / `failed`,
 * 03 §4.8): apply the mark again, or confirm that the payment was entered by hand.
 */
export const EasSyncDialog: FC<TEasSyncDialogProps> = ({ open, payment, onClose, onDone }) => {
	const [action, setAction] = useState<TPgEasSyncAction>('apply');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		setAction('apply');
		setError(null);
		setBusy(false);
	}, [open]);

	const submit = async (note: string) => {
		setBusy(true);
		setError(null);
		try {
			const updated = await api.paymentGateway.easSyncPayment({
				transactionId: payment.transactionId,
				body: { action, note },
			});
			notificationContainer(
				action === 'apply' ? 'The payment is being applied to EAS' : 'Marked as done manually',
				'success',
			);
			onDone(updated);
		} catch (e) {
			setError(getPgErrorMessage(e, 'EAS sync failed'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<PgConfirmDialog
			open={open}
			title="EAS sync"
			confirmLabel={action === 'apply' ? 'Apply to EAS' : 'Mark as done manually'}
			reasonLabel="Note (shown in the audit log)"
			busy={busy}
			error={error}
			onConfirm={(note) => void submit(note)}
			onClose={onClose}
			description={
				<Alert severity="warning" sx={{ mb: 2 }}>
					EAS sync status is <b>{humanize(payment.easSyncStatus)}</b>: paying this invoice again is
					blocked until the mark is resolved. Check the invoice in EAS first.
				</Alert>
			}
		>
			<RadioGroup value={action} onChange={(e) => setAction(e.target.value as TPgEasSyncAction)}>
				<FormControlLabel
					value="apply"
					control={<Radio />}
					label="Apply to EAS: repeat the payment mark in EAS"
					disabled={busy}
				/>
				<FormControlLabel
					value="mark_done"
					control={<Radio />}
					label="Mark as done manually: the payment is already entered in EAS by hand"
					disabled={busy}
				/>
			</RadioGroup>
		</PgConfirmDialog>
	);
};
